import { useState } from 'react';
import {
  AlertTriangle,
  Bell,
  Check,
  CheckCircle2,
  ChevronDown,
  Clock,
  Copy,
  FileText,
  Link2,
  Mail,
  MailOpen,
  MoreHorizontal,
  PackageMinus,
  PackagePlus,
  RotateCw,
  Scissors,
  Trash2,
  XCircle,
} from 'lucide-react';
import { Link } from 'react-router-dom';
import { toast } from 'sonner';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import {
  Collapsible,
  CollapsibleContent,
  CollapsibleTrigger,
} from '@/components/ui/collapsible';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import { Tooltip, TooltipContent, TooltipTrigger } from '@/components/ui/tooltip';
import { cn } from '@/lib/utils';
import { useIsTruncated } from '@/hooks/useIsTruncated';
import { useNotificationStore } from '../store/notificationStore';
import { absoluteLabel, countdownTo, relativeFromMicros } from '../lib/notificationTime';
import { notificationHref } from '../lib/entityHref';
import {
  canCancel,
  canReconcile,
  canRetry,
  type Notification,
  type NotificationState,
} from '../types/notification.types';

/**
 * One journey, as a row — the shared unit of both the popover and the `/notifications` page.
 *
 * ⚠️⚠️ `'use no memo';` — SECOND occurrence in this codebase (`components/ui/data-table-row.tsx:58`
 * is the first), and it must survive review. See 86d39bx22 / 86d3d1z40.
 *
 * This row is handed a `notification` prop and morphs in place: an SSE frame replaces the object,
 * and the recovery buttons are derived from `notification.actions`. The React Compiler auto-memoizes
 * on props, and every path that reaches here — a store action, an SSE upsert, a 400-with-body — is a
 * mutable-state read the compiler cannot track as a dependency. Memoized, the buttons freeze in the
 * state they first rendered in while the row's data moves underneath them, which on this component
 * means offering Retry on a posting that may already exist in NetSuite.
 *
 * <b>Keyed by `notification.id` at every call site, never `jobId`</b> — `jobId` is `long?` and
 * nullable by schema (comment / procurement / system rows carry none), and a nullable column cannot
 * key a collection.
 */
export function NotificationRow({
  notification,
  nowMs,
  variant = 'compact',
  onNavigate,
}: {
  notification: Notification;
  /**
   * A server-aligned instant from the list's single `useServerNow()`. A PROP rather than a hook call
   * so this component stays a pure function of its inputs — and so fifty rows do not own fifty
   * timers.
   */
  nowMs: number;
  /** `full` is the page: same anatomy, more room to breathe. */
  variant?: 'compact' | 'full';
  /** Called when the row navigates away — the popover uses it to close itself. */
  onNavigate?: () => void;
}) {
  'use no memo';

  const retry = useNotificationStore((s) => s.retry);
  const cancel = useNotificationStore((s) => s.cancel);
  const reconcile = useNotificationStore((s) => s.reconcile);

  const [busy, setBusy] = useState(false);
  const [message, setMessage] = useState<string | null>(null);

  /**
   * Instant acknowledgement: `busy` flips before the request leaves, so the click has visible
   * feedback well inside 100ms rather than waiting on a NetSuite round trip.
   *
   * ⚠️ The refusal message is state, not a thrown error. The store has already replaced this row
   * with the server's corrected copy by the time the message lands — showing it inline beside a row
   * that now reads correctly is the entire point of N4's 400-with-body.
   */
  const run = async (action: (id: number) => Promise<string | undefined>) => {
    setBusy(true);
    setMessage(null);
    try {
      setMessage((await action(notification.id)) ?? null);
    } catch {
      setMessage('Could not reach the server. Nothing was changed — try again.');
    } finally {
      setBusy(false);
    }
  };

  const tone = severityOf(notification);
  const isFull = variant === 'full';

  // Derived in render from the directly-passed row. The FIRST permitted verb is the primary button —
  // the server orders `actions` by what it wants pressed, so this needs no client-side ranking.
  const primaryAction = notification.actions[0];

  return (
    <div
      className={cn(
        'group relative flex gap-2.5 rounded-md border border-l-2 bg-card transition-colors',
        tone.border,
        notification.unread && 'bg-info/5',
        isFull ? 'p-4' : 'p-3',
      )}
    >
      <IdentitySlot notification={notification} tone={tone} />

      <div className="min-w-0 flex-1">
        {/* ⚠️ This padding reserves space for `RowAside`, which is ABSOLUTE and therefore invisible
            to this flex row. Only the TITLE line pads around the timestamp cluster — everything
            below it (error text, suggestion, chips, buttons) runs the full width, which is the whole
            point of lifting the cluster out of the flow.

            ⚠️ 64px, where the design says 54 — margin, NOT a fix for a measured break. Past 7 days
            `relativeFromMicros` falls back to a LOCALE DATE ("Aug 25"), which the prototype's clock
            never renders. Measured in-browser: that label plus an unread dot makes the widest
            cluster 54.5px, which still clears the design's own reserve by ~7px. The extra 8px buys
            room for locales whose short date is longer than en-US's ("25 de ago."), where 7px is
            not enough. If you are tempted to reclaim it, measure that locale first. */}
        <div className={cn('flex items-start gap-2', isFull ? 'pr-16' : 'pr-11')}>
          <RowTitle notification={notification} isFull={isFull} onNavigate={onNavigate} />
          <StatusChips notification={notification} />
        </div>

        {notification.body && <RowBody body={notification.body} isFull={isFull} />}

        {notification.entityRef && (
          <span className="mt-1.5 inline-flex items-center rounded border border-border bg-muted px-1.5 py-0.5 text-xs font-medium text-muted-foreground">
            {notification.entityRef}
          </span>
        )}

        {/*
          THE THREE-LAYER FAILURE ANATOMY, in the order someone reads it.

          1. the human summary — always shown, never a stack trace;
          2. the suggestion — what to do, sitting immediately above the button that does it;
          3. Technical details — collapsed, for the person who has to escalate.

          Layers 1 and 2 are SERVER COPY (`NotificationCopyMap`), so the wording is the same in the
          popover, on the page, and in any future channel. The client never composes failure prose.
        */}
        {notification.errorSummary && (
          <p className="mt-1.5 text-xs text-destructive">{notification.errorSummary}</p>
        )}
        {notification.suggestion && (
          <p className="mt-1 text-xs text-muted-foreground">{notification.suggestion}</p>
        )}

        <RetryCountdown notification={notification} nowMs={nowMs} />

        {/*
          Rendered from the server's `actions` array — ⛔ never recomputed from `reach`.
          `PostingJourney.Actions` is the one implementation that both fills this array and gates the
          endpoint, so the buttons and the server cannot disagree. A row with no permitted verbs
          renders NO button, not a disabled one: a disabled control invites a user to hunt for the
          permission they are missing when in fact there is nothing to do.
        */}
        {notification.actions.length > 0 && (
          <div className="relative z-10 mt-2 flex flex-wrap gap-2">
            {canRetry(notification) && (
              <RecoveryButton
                label="Retry"
                hint="Post this again. Nothing reached NetSuite, so it is safe."
                icon={RotateCw}
                busy={busy}
                primary={primaryAction === 'retry'}
                onClick={() => run(retry)}
              />
            )}
            {canCancel(notification) && (
              <RecoveryButton
                label="Cancel"
                hint="Close this off. Nothing was created in NetSuite."
                icon={XCircle}
                busy={busy}
                primary={primaryAction === 'cancel'}
                onClick={() => run(cancel)}
              />
            )}
            {canReconcile(notification) && (
              <RecoveryButton
                label="Reconcile"
                hint="Link the record NetSuite already has. Do not post again."
                icon={Link2}
                busy={busy}
                primary={primaryAction === 'reconcile'}
                onClick={() => run(reconcile)}
              />
            )}
          </div>
        )}

        <TechnicalDetails notification={notification} />

        {/* ⚠️ A warning-TINTED BOX, not `text-warning-foreground` on the card: that token is L=12%
            in every theme (it is made to sit ON bg-warning), so on the dark card it was invisible —
            the user watched their buttons change with no stated reason (2026-08-25 review). */}
        {message && (
          <p
            className="mt-2 rounded border border-warning/30 bg-warning/10 px-2 py-1.5 text-xs text-foreground"
            role="status"
          >
            {message}
          </p>
        )}
      </div>

      <RowAside notification={notification} nowMs={nowMs} isFull={isFull} />
    </div>
  );
}

/**
 * The title, and the row's link to the record it is about.
 *
 * <b>The stretched-link pattern</b> — a real `<Link>` whose `::after` covers the whole row. It gives
 * a full-row click target while keeping ONE interactive element in the accessibility tree: wrapping
 * the row in a click handler with `role="link"` would be a lie to screen readers, and nesting the
 * recovery buttons inside a real anchor is invalid HTML that browsers resolve by dropping the inner
 * controls. Everything clickable inside the row sits on `relative z-10` so it stays above the sheet.
 *
 * ⛔ A row with nowhere to go still ANSWERS A CLICK. `entityHrefFor` returns null when the type is
 * unknown or the entity reference is gone, and a silent no-op there reads as a broken feature — the
 * user clicks, nothing happens, and they click again. It says so instead.
 */
function RowTitle({
  notification,
  isFull,
  onNavigate,
}: {
  notification: Notification;
  isFull: boolean;
  onNavigate?: () => void;
}) {
  const href = notificationHref(notification);
  const { ref, isTruncated } = useIsTruncated<HTMLElement>();

  const className = cn(
    'min-w-0 flex-1 text-left text-foreground',
    // 12px in the 264px panel, 14px on the page. Titles are sentences, and at 14px a two-line clamp
    // in a 264px column cut most of them ("Material issuance posting cancelled" became "Material
    // issuance…"). Dropping a step fits roughly three more words per line; the hierarchy against the
    // body survives on weight and colour, which is what carried it in the design's own dense panel.
    isFull ? 'text-sm' : 'text-xs',
    notification.unread ? 'font-semibold' : 'font-medium',
    // ⚠️ TWO LINES, not one ellipsis (2026-08-25). These titles are sentences — "Sales order may
    // already exist in NetSuite" — and truncating at one line cut them exactly where the meaning is.
    // ⛔ `line-clamp-2` sets `overflow:hidden`, which is safe for the stretched-link `::after` ONLY
    // because this element is not positioned: an abspos descendant escapes a STATIC ancestor's
    // overflow. Add `relative` here and the whole-row click target silently disappears.
    // `break-words` travels WITH the clamp: once the title stopped being a single nowrap line, an
    // unbroken token longer than the row (a document ref, a pasted URL) had nowhere to break and was
    // simply swallowed by the clamp's `overflow:hidden` instead of wrapping.
    !isFull && 'line-clamp-2 break-words leading-snug',
  );

  if (!href) {
    return (
      <TruncationTooltip text={notification.title} isTruncated={isTruncated}>
        <button
          ref={ref as React.Ref<HTMLButtonElement>}
          type="button"
          className={className}
          onClick={() => toast.info('This document is no longer available.')}
        >
          {notification.title}
        </button>
      </TruncationTooltip>
    );
  }

  return (
    <TruncationTooltip text={notification.title} isTruncated={isTruncated}>
      <Link
        ref={ref as React.Ref<HTMLAnchorElement>}
        to={href}
      onClick={() => {
        // Opening a notification IS reading it — the inbox convention every mail client trained
        // users into, and its absence read as a bug (owner, 2026-08-25): rows stayed bold after
        // being clicked while "Mark all as read" worked. Fire-and-forget on purpose: markRead is
        // optimistic (row flips instantly) and restores itself on failure, and a failed mark must
        // never block the navigation the user actually asked for — the row simply stays unread.
        if (notification.unread) {
          void useNotificationStore
            .getState()
            .markRead([notification.id])
            .catch(() => {});
        }
        onNavigate?.();
      }}
        className={cn(className, 'after:absolute after:inset-0 after:content-[""] hover:underline')}
      >
        {notification.title}
      </Link>
    </TruncationTooltip>
  );
}

/**
 * Wraps a control in a tooltip ONLY while its text is actually cut off.
 *
 * ⚠️ Both branches render the child unchanged — no extra wrapper element either way. Putting a
 * `<span>` around the title in the truncated branch alone would move the stretched-link `::after`
 * into a different containing block and silently shrink the whole-row click target on exactly the
 * rows that need it most.
 */
function TruncationTooltip({
  text,
  isTruncated,
  children,
}: {
  text: string;
  isTruncated: boolean;
  children: React.ReactElement;
}) {
  if (!isTruncated) return children;

  return (
    <Tooltip>
      <TooltipTrigger asChild>{children}</TooltipTrigger>
      {/* `side="bottom"` so it never lands on the row above, which is another notification the user
          may be reading. Wrapped, because the whole point is showing a sentence that did not fit. */}
      <TooltipContent side="bottom" className="max-w-xs whitespace-normal">
        {text}
      </TooltipContent>
    </Tooltip>
  );
}

/**
 * The body line. Three lines in the panel rather than two — the panel is the narrow surface, so it
 * is the one that needs the extra line most — and the same measured tooltip as the title when even
 * three does not hold it.
 */
function RowBody({ body, isFull }: { body: string; isFull: boolean }) {
  const { ref, isTruncated } = useIsTruncated<HTMLParagraphElement>();

  return (
    <TruncationTooltip text={body} isTruncated={isTruncated}>
      <p
        ref={ref}
        className={cn('mt-0.5 text-xs text-muted-foreground', !isFull && 'line-clamp-3 break-words')}
      >
        {body}
      </p>
    </TruncationTooltip>
  );
}

/**
 * The 28px identity slot — deliberately POLYMORPHIC.
 *
 * Today every row is a posting, so it renders the flow's icon in a circle tinted by severity. When
 * Comments and @mentions arrive the same slot takes an avatar, which is why it is a slot rather than
 * an inline icon: the row grammar does not change when the source does.
 */
function IdentitySlot({ notification, tone }: { notification: Notification; tone: SeverityTone }) {
  const { Icon, label } = describeType(notification.type);

  return (
    <Tooltip>
      <TooltipTrigger asChild>
        <span
          className={cn(
            // relative z-10 lifts the slot above the stretched link's ::after overlay — without it the
            // overlay wins hit-testing and this icon-only control's tooltip can never fire.
            // 28px, down from 32px (2026-08-25 density pass) — the glyph inside stays 16px, so the
            // circle tightens around it rather than the icon shrinking with it.
            'relative z-10 flex h-7 w-7 flex-shrink-0 items-center justify-center rounded-full',
            tone.slot,
          )}
          aria-label={label}
          title={label}
        >
          <Icon className="h-4 w-4" aria-hidden="true" />
        </span>
      </TooltipTrigger>
      <TooltipContent side="right">{label}</TooltipContent>
    </Tooltip>
  );
}

/**
 * Timestamp · unread dot · ⋯ — ABSOLUTE, not a flex column (2026-08-25 layout pass).
 *
 * The ⋯ sits LAYERED over the timestamp and fades in on hover or keyboard focus, so revealing it
 * cannot reflow the row — a menu that pushes the timestamp sideways makes a list of fifty rows feel
 * like it is breathing.
 *
 * In the flow this cluster reserved a ~55px gutter for the ENTIRE height of the row, so a two-line
 * error summary, the suggestion, the entity chip and the recovery buttons were all squeezed by a
 * column that only the first line ever needed. Lifting it out gives every line below the title the
 * full width back; the title alone pads around it (`pr-16` on the title row).
 *
 * ⛔ The row must stay `relative` — this is positioned against it.
 */
function RowAside({
  notification,
  nowMs,
  isFull,
}: {
  notification: Notification;
  nowMs: number;
  isFull: boolean;
}) {
  return (
    <div className="absolute right-2.5 top-2.5 z-10 flex items-start gap-1.5">
      <Tooltip>
        <TooltipTrigger asChild>
          {/* `whitespace-nowrap` is belt-and-braces, not a fix for an observed break: the cluster
              is absolute and shrink-to-fit, so it resolves to max-content and "Aug 25" does not
              wrap today. It pins that against a future narrower cluster or a longer stamp. */}
          <span
            className={cn(
              'whitespace-nowrap text-muted-foreground transition-opacity group-hover:opacity-0 group-focus-within:opacity-0',
              // 9px in the 264px panel. The stamp is the thing the title line reserves width FOR, so
              // shrinking it is the cheapest width the row can buy back — and it is the least
              // important text in the row, with the exact time one hover away either way.
              isFull ? 'text-xs' : 'text-2xs',
            )}
          >
            {relativeFromMicros(notification.createdAtMicros, nowMs)}
          </span>
        </TooltipTrigger>
        <TooltipContent side="left">{absoluteLabel(notification.createdAt)}</TooltipContent>
      </Tooltip>

      {notification.unread && (
        <span
          className="mt-1 h-2 w-2 flex-shrink-0 rounded-full bg-info"
          aria-label="Unread"
          title="Unread"
        />
      )}

      <div className="absolute -top-1 right-0 z-10 opacity-0 transition-opacity focus-within:opacity-100 group-hover:opacity-100 group-focus-within:opacity-100">
        <RowMenu notification={notification} />
      </div>
    </div>
  );
}

/**
 * ⛔ EXACTLY THREE ITEMS. Mark as read/unread (one toggle, not two entries), Mark as done, Remove.
 *
 * Recovery verbs deliberately do NOT live in here. They are consequential — one of them can create a
 * record in the live company file — and consequential actions belong on visible buttons whose copy
 * explains them, not behind a ⋯ where they are one mis-click away.
 */
function RowMenu({ notification }: { notification: Notification }) {
  const markRead = useNotificationStore((s) => s.markRead);
  const markUnread = useNotificationStore((s) => s.markUnread);
  const markDone = useNotificationStore((s) => s.markDone);
  const remove = useNotificationStore((s) => s.remove);

  // Each store action is optimistic, so the row repaints on the click and this only has to say
  // something when the server disagrees afterwards.
  const guard = (run: () => Promise<void>, failure: string) => () => {
    void run().catch(() => toast.error(failure));
  };

  return (
    <DropdownMenu>
      <Tooltip>
        <TooltipTrigger asChild>
          <DropdownMenuTrigger asChild>
            <Button
              variant="ghost"
              size="icon"
              className="h-7 w-7 bg-card"
              aria-label="Notification actions"
            >
              <MoreHorizontal className="h-4 w-4" aria-hidden="true" />
            </Button>
          </DropdownMenuTrigger>
        </TooltipTrigger>
        <TooltipContent side="left">Notification actions</TooltipContent>
      </Tooltip>

      <DropdownMenuContent align="end" className="w-44">
        {notification.unread ? (
          <DropdownMenuItem
            onSelect={guard(() => markRead([notification.id]), 'Could not mark as read')}
          >
            <MailOpen className="mr-2 h-4 w-4" aria-hidden="true" />
            Mark as read
          </DropdownMenuItem>
        ) : (
          <DropdownMenuItem
            onSelect={guard(() => markUnread([notification.id]), 'Could not mark as unread')}
          >
            <Mail className="mr-2 h-4 w-4" aria-hidden="true" />
            Mark as unread
          </DropdownMenuItem>
        )}

        <DropdownMenuItem
          onSelect={guard(() => markDone(notification.id), 'Could not mark as done')}
        >
          <Check className="mr-2 h-4 w-4" aria-hidden="true" />
          Mark as done
        </DropdownMenuItem>

        <DropdownMenuItem
          className="text-destructive focus:text-destructive"
          onSelect={guard(() => remove(notification.id), 'Could not remove this notification')}
        >
          <Trash2 className="mr-2 h-4 w-4" aria-hidden="true" />
          Remove
        </DropdownMenuItem>
      </DropdownMenuContent>
    </DropdownMenu>
  );
}

/**
 * The one chip of the status-mark vocabulary that is not carried by tint or accent.
 *
 * `Done` is the user's own archive gesture, and the row leaves the main list for the Done tab.
 *
 * ⚠️ `Resolved` used to render here too — the server applies it the moment a needs-action job
 * settles (the N9 reconciler, not a click). Removed 2026-08-27 on the owner's call: it restated what
 * the row's own copy already says ("Cutting list is now in NetSuite"). The FIELD is not dead —
 * `severityOf` still reads it to drop the red needs-action accent once the job settles, and that
 * accent is now the only place resolution shows.
 */
function StatusChips({ notification }: { notification: Notification }) {
  // The shared `Badge` primitive rather than a hand-rolled span — UI gate #1, and its `secondary`
  // variant already carries the tokens this chip needs in all three themes.
  if (notification.done) {
    return (
      <Badge variant="secondary" className="flex-shrink-0 px-1.5">
        Done
      </Badge>
    );
  }

  return null;
}

/** "Next attempt in 6m (2 of 3)", on the server's clock. */
function RetryCountdown({ notification, nowMs }: { notification: Notification; nowMs: number }) {
  const countdown = countdownTo(notification.nextRetryAt, nowMs);
  if (!countdown) return null;

  return (
    <p className="mt-1 text-xs text-info">
      Next attempt {countdown}
      {notification.maxAttempts > 0 && ` (${notification.attempt} of ${notification.maxAttempts})`}
    </p>
  );
}

/**
 * Layer three of the failure anatomy: everything an escalation needs, folded away from everyone else.
 *
 * ⚠️ There is deliberately no idempotency key here — it is not on the wire. `NotificationDto` carries
 * the job id, the attempt counters, the failure class and the frozen `timeline[]`, and the job id is
 * what a support conversation actually starts from. Do NOT add a client-side guess at the key.
 */
function TechnicalDetails({ notification }: { notification: Notification }) {
  const [open, setOpen] = useState(false);

  const hasDetail =
    notification.jobId !== null ||
    notification.failureClass !== null ||
    notification.timeline.length > 0;

  // Only failures earn the disclosure. A posted row with a clean timeline has nothing behind it worth
  // a control, and offering one on every row buries the failures that do.
  if (!hasDetail || !notification.errorSummary) return null;

  const copyable = [
    `Notification: ${notification.id}`,
    notification.jobId !== null ? `Outbox job: ${notification.jobId}` : null,
    `Type: ${notification.type}`,
    `State: ${notification.state}${notification.reach ? ` (reach: ${notification.reach})` : ''}`,
    notification.failureClass ? `Failure class: ${notification.failureClass}` : null,
    `Attempt: ${notification.attempt} of ${notification.maxAttempts}`,
    notification.nsNumber ? `NetSuite record: ${notification.nsNumber}` : null,
    `Error: ${notification.errorSummary}`,
    ...notification.timeline.map((t) => `  ${t.at} — ${t.label}${t.detail ? `: ${t.detail}` : ''}`),
  ]
    .filter(Boolean)
    .join('\n');

  return (
    <Collapsible open={open} onOpenChange={setOpen} className="relative z-10 mt-2">
      <CollapsibleTrigger asChild>
        <Button variant="ghost" size="sm" className="h-6 px-1 text-xs text-muted-foreground">
          <ChevronDown
            className={cn('mr-1 h-3 w-3 transition-transform', open && 'rotate-180')}
            aria-hidden="true"
          />
          Technical details
        </Button>
      </CollapsibleTrigger>

      <CollapsibleContent className="mt-1.5 space-y-1.5 rounded border border-border bg-muted p-2">
        <dl className="space-y-0.5 text-xs text-muted-foreground">
          {notification.jobId !== null && (
            <DetailLine label="Job" value={String(notification.jobId)} />
          )}
          {notification.failureClass && (
            <DetailLine label="Failure" value={notification.failureClass} />
          )}
          <DetailLine
            label="Attempt"
            value={`${notification.attempt} of ${notification.maxAttempts}`}
          />
          {notification.nsNumber && <DetailLine label="NetSuite" value={notification.nsNumber} />}
        </dl>

        {notification.timeline.length > 0 && (
          <ol className="space-y-0.5 border-t border-border pt-1.5 text-xs text-muted-foreground">
            {notification.timeline.map((entry, index) => (
              // Index-keyed on purpose: the timeline is FROZEN at settle (the payload scrub destroys
              // the originals), so it is append-only history that never reorders.
              <li key={`${entry.at}-${index}`}>
                <span className="font-medium text-foreground">{entry.label}</span>
                {entry.detail && <span> — {entry.detail}</span>}
              </li>
            ))}
          </ol>
        )}

        <Button
          variant="outline"
          size="sm"
          className="h-6 w-full text-xs"
          onClick={() => {
            void navigator.clipboard
              .writeText(copyable)
              .then(() => toast.success('Details copied'))
              .catch(() => toast.error('Could not copy — select the text instead'));
          }}
        >
          <Copy className="mr-1 h-3 w-3" aria-hidden="true" />
          Copy details
        </Button>
      </CollapsibleContent>
    </Collapsible>
  );
}

function DetailLine({ label, value }: { label: string; value: string }) {
  return (
    <div className="flex gap-2">
      <dt className="w-16 flex-shrink-0">{label}</dt>
      <dd className="min-w-0 break-all font-mono text-foreground">{value}</dd>
    </div>
  );
}

function RecoveryButton({
  label,
  hint,
  icon: Icon,
  busy,
  primary,
  onClick,
}: {
  label: string;
  hint: string;
  icon: typeof RotateCw;
  busy: boolean;
  primary: boolean;
  onClick: () => void;
}) {
  return (
    <Tooltip>
      <TooltipTrigger asChild>
        {/* ⚠️ QUIET BY DESIGN (2026-08-25). These were a filled Capitol-Red slab + an outlined
            secondary. In a list where the row's SEVERITY is already carried by the left accent and
            the identity tint, a saturated button competed with it — the loudest thing in a failed
            row was the control, not the failure. The primary verb is now a tinted text button and
            the secondary is plain muted text, so colour in this row means severity and nothing else.
            ⛔ `ghost` for both: `outline` would put the border back. */}
        <Button
          size="sm"
          variant="ghost"
          disabled={busy}
          onClick={onClick}
          className={cn(
            // ⚠️ `[&_svg]:size-3` is load-bearing, not decoration. The Button base sets
            // `[&_svg]:size-4` (button.tsx:8) at specificity (0,1,1), which BEATS a plain `h-3 w-3`
            // on the icon (0,1,0) — so the glyph silently stayed 16px inside a 24px control, the
            // opposite of a density pass. Same trick, same reason as StationStatus.tsx:123.
            'h-6 gap-1 rounded px-2 text-xs font-semibold [&_svg]:size-3',
            primary
              // ⚠️ `text-primary-INK`, not `text-primary`. The tint and the brand red are the same
              // hue, so red-on-tint lands at ~3.2:1 — unreadable at 12px. Ink is that same red moved
              // in lightness only (L41 light/industrial, L67 dark) and clears 4.5:1 even in the
              // worst state: hover tint over an unread row. The VARIANT is unchanged — still small,
              // still tinted-not-filled — only the ink is legible. Measured, not guessed.
              ? 'bg-primary/10 text-primary-ink hover:bg-primary/15 hover:text-primary-ink'
              // ⛔ The hover is PINNED, and it has to be. `ghost` ships
              // `hover:bg-accent hover:text-accent-foreground`; with nothing to collide with,
              // tailwind-merge keeps it and the "plain muted text" secondary paints a filled slab
              // on hover — exactly the loud control this pass removed.
              : 'text-muted-foreground hover:bg-transparent hover:text-foreground',
          )}
        >
          <Icon className="h-3 w-3" aria-hidden="true" />
          {busy ? 'Working…' : label}
        </Button>
      </TooltipTrigger>
      <TooltipContent>{hint}</TooltipContent>
    </Tooltip>
  );
}

// ── Presentation vocabulary ──────────────────────────────────────────────────

interface SeverityTone {
  /** The 2px left accent. */
  border: string;
  /** The identity slot's tint + glyph colour. */
  slot: string;
}

/**
 * The row's severity, as tokens.
 *
 * ⚠️ Needs-action wins over everything, including `state`. A failed posting nobody has discharged is
 * the most important thing in the list regardless of what its journey says, and the red accent is
 * what the pinned section's promise rests on.
 *
 * ⛔ Semantic tokens only, and every opacity modifier is on Tailwind's step-of-5 scale (or one of the
 * three registered in `tailwind.config.js`). An off-scale modifier emits NO rule at all and paints
 * transparent — silently, with nothing in tsc or the linter to catch it.
 */
function severityOf(n: Notification): SeverityTone {
  if (n.needsAction && !n.resolved) {
    return { border: 'border-l-destructive', slot: 'bg-destructive/10 text-destructive' };
  }
  if (n.done) {
    return { border: 'border-l-border', slot: 'bg-muted text-muted-foreground' };
  }

  switch (n.state) {
    case 'posted':
      return { border: 'border-l-success', slot: 'bg-success/10 text-success' };
    case 'failed':
    case 'reconcile':
      return { border: 'border-l-warning', slot: 'bg-warning/10 text-warning' };
    case 'cancelled':
      return { border: 'border-l-border', slot: 'bg-muted text-muted-foreground' };
    default:
      return { border: 'border-l-info', slot: 'bg-info/10 text-info' };
  }
}

/**
 * The flow behind the row, as an icon and an accessible name.
 *
 * Falls back rather than throwing on an unknown type: the server's type vocabulary can grow a member
 * (Comments, procurement) before this file learns about it, and a generic bell beats a blank slot or
 * a crashed list.
 */
function describeType(type: string): { Icon: typeof Bell; label: string } {
  switch (type) {
    case 'posting.so':
      return { Icon: FileText, label: 'Sales order' };
    case 'posting.cl':
      return { Icon: Scissors, label: 'Cutting list' };
    case 'posting.mis':
      return { Icon: PackageMinus, label: 'Material issuance' };
    case 'posting.ir':
      return { Icon: PackagePlus, label: 'Item receipt' };
    default:
      return { Icon: Bell, label: 'Notification' };
  }
}

/**
 * The journey state as a glyph — kept for surfaces that want the state rather than the flow.
 * Icon-only, so it carries a tooltip AND a matching `aria-label`.
 */
export function StateIcon({ state }: { state: NotificationState }) {
  const { Icon, tone, label } = describeState(state);

  return (
    <Tooltip>
      <TooltipTrigger asChild>
        <span className="mt-0.5 flex-shrink-0" aria-label={label} title={label}>
          <Icon className={cn('h-4 w-4', tone)} aria-hidden="true" />
        </span>
      </TooltipTrigger>
      <TooltipContent>{label}</TooltipContent>
    </Tooltip>
  );
}

function describeState(state: NotificationState) {
  switch (state) {
    case 'posted':
      return { Icon: CheckCircle2, tone: 'text-success', label: 'Posted to NetSuite' };
    case 'failed':
      return { Icon: XCircle, tone: 'text-destructive', label: "Didn't post" };
    case 'reconcile':
      return { Icon: AlertTriangle, tone: 'text-warning', label: 'Needs reconciling' };
    case 'cancelled':
      return { Icon: XCircle, tone: 'text-muted-foreground', label: 'Cancelled' };
    case 'retrying':
      return { Icon: RotateCw, tone: 'text-info', label: 'Retrying' };
    case 'posting':
      return { Icon: Clock, tone: 'text-info', label: 'Posting now' };
    default:
      return { Icon: Clock, tone: 'text-muted-foreground', label: 'Queued for NetSuite' };
  }
}
